import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ProgressBar } from "@/components/ProgressBar";
import {
  nextSession,
  masteryNextSession,
  dayCompletion,
  overallStats,
  masteryStadioProgress,
  isDayComplete,
  getProgram,
  getActiveProgramId,
  isProgramVisible,
} from "@/data/program";
import { ArrowRight, Flame, CheckCircle2, Dumbbell } from "lucide-react";

export default function Today() {
  const { user, completed, stats: cumulativeStats } = useAuth();
  const navigate = useNavigate();

  const program = getProgram(getActiveProgramId());

  useEffect(() => {
    if (program && !isProgramVisible(program, user?.email)) {
      navigate("/select", { replace: true });
    }
  }, [program, user, navigate]);
  const day = program?.progressionType === "mastery"
    ? masteryNextSession(program, completed)
    : nextSession(program, completed);
  const isMastery = program?.progressionType === "mastery";
  const calStats = overallStats(program, completed);
  const masteryProgress = isMastery ? masteryStadioProgress(program, completed) : null;

  if (!day) return null;

  const comp = dayCompletion(day, completed);
  const allDone = isDayComplete(day, completed);

  return (
    <div className="px-5 pt-12 animate-slideup">
      <p className="font-head text-sm uppercase tracking-[0.25em] text-zinc-500">
        Ciao {user?.name?.split(" ")[0] || "Atleta"}
      </p>
      <h1 className="font-head text-4xl font-black uppercase tracking-tight text-white">
        Allenamento di oggi
      </h1>

      {/* Today's session card */}
      <button
        data-testid="today-session-card"
        onClick={() => navigate(`/session/${day.id}`)}
        className="mt-6 w-full text-left border border-zinc-800 bg-[#141414] active:scale-[0.99] transition-transform"
      >
        <div className="flex items-center justify-between px-5 pt-5">
          <div>
            <p className="font-head text-xs uppercase tracking-widest text-[#fa003f]">
              {day.phase.name} · {day.week.name}
            </p>
            <h2 className="font-head text-4xl font-bold uppercase tracking-tight text-white">
              {day.label}
            </h2>
          </div>
          <div className="flex h-14 w-14 items-center justify-center bg-[#fa003f]">
            {allDone ? (
              <CheckCircle2 size={28} className="text-white" />
            ) : (
              <Flame size={28} className="text-white" />
            )}
          </div>
        </div>

        <div className="px-5 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-zinc-400">{day.exercises.length} esercizi</span>
            <span className="font-head text-lg text-white">
              {comp.done}/{comp.total}
            </span>
          </div>
          <ProgressBar pct={comp.pct} glow />
        </div>

        <div className="flex items-center justify-between border-t border-zinc-800 px-5 py-4">
          <span className="font-head text-lg uppercase tracking-wide text-white">
            {allDone ? "Ripeti sessione" : comp.done > 0 ? "Continua" : "Inizia adesso"}
          </span>
          <ArrowRight size={22} className="text-[#fa003f]" />
        </div>
      </button>

      {/* Overall stats */}
      <h3 className="mt-8 font-head text-xl uppercase tracking-wide text-zinc-400">
        Il tuo percorso
      </h3>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="border border-zinc-800 bg-[#141414] p-4">
          <Dumbbell size={20} className="text-[#fa003f] mb-2" />
          <p className="font-head text-4xl font-black text-white tabular-nums">
            {isMastery ? cumulativeStats.workouts : calStats.doneDays}
          </p>
          <p className="text-xs uppercase tracking-wider text-zinc-500">
            {isMastery ? "Allenamenti" : `Giorni / ${calStats.totalDays}`}
          </p>
        </div>
        <div className="border border-zinc-800 bg-[#141414] p-4">
          <CheckCircle2 size={20} className="text-[#fa003f] mb-2" />
          <p className="font-head text-4xl font-black text-white tabular-nums">
            {isMastery ? cumulativeStats.exercises : calStats.doneEx}
          </p>
          <p className="text-xs uppercase tracking-wider text-zinc-500">
            Esercizi fatti
          </p>
        </div>
      </div>

      <div className="mt-3 border border-zinc-800 bg-[#141414] p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm uppercase tracking-wider text-zinc-400">
            {isMastery && masteryProgress
              ? `Padronanza — ${masteryProgress.phase.name}`
              : "Completamento totale"}
          </span>
          <span className="font-head text-2xl text-[#fa003f]">
            {isMastery ? masteryProgress.pct : calStats.pct}%
          </span>
        </div>
        <ProgressBar pct={isMastery ? masteryProgress.pct : calStats.pct} />
      </div>
    </div>
  );
}
